<template>
  <div>
    <!-- A clickable header (e.g. Accordion) renders as a real button so it is
         keyboard-operable and can announce its expanded state. Plain sections
         render a bare heading row instead of a boxed bar. -->
    <component
      :is="clickable ? 'button' : 'div'"
      v-bind="clickable ? { type: 'button', 'aria-expanded': expanded } : {}"
      class="flex items-center justify-between gap-2"
      :class="[
        clickable
          ? 'w-full h-10 px-3 rounded-lg border border-container-border dark:border-container-border-dark bg-box-container-bg dark:bg-box-container-bg-dark shadow-card cursor-pointer text-left transition-colors hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 dark:focus-visible:ring-accent-dark/40'
          : 'min-h-7',
        headerClass,
      ]"
      @click="clickable && $emit('headerClick')"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <img
          v-if="imageSrc"
          :src="imageSrc"
          :alt="imageAlt || title"
          class="h-5 w-9 object-contain shrink-0"
        />
        <component
          :is="clickable ? 'span' : 'h3'"
          class="truncate font-semibold text-heading dark:text-heading-dark"
          :class="clickable ? 'text-sm' : 'text-base'"
        >
          {{ title }}
        </component>
      </div>
      <component
        v-if="icon"
        :is="icon"
        aria-hidden="true"
        class="shrink-0 text-muted-text dark:text-muted-text-dark"
      />
    </component>
    <div class="mt-3 space-y-4" v-if="$slots.default">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Component } from "vue";

interface Props {
  title: string;
  icon?: Component;
  imageSrc?: string;
  imageAlt?: string;
  headerClass?: string;
  /** Renders the header as a button that emits `headerClick`. */
  clickable?: boolean;
  /** Expanded state announced on the header button when `clickable`. */
  expanded?: boolean;
}

defineProps<Props>();
defineEmits(["headerClick"]);
</script>
